import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin, BedDouble, DoorOpen, BadgeIndianRupee, Phone,
  PlusCircle, Loader2, AlertCircle, Lock, RefreshCw,
  Pencil, Trash2, CheckCircle2, X, Eye,
} from 'lucide-react';
import { Button } from '../ui/button';
import ExpandableText from '../ui/ExpandableText';
import {
  getPgSaleListings,
  createPgSaleListing,
  updatePgSaleListing,
  deletePgSaleListing,
} from '../../api/services/pgTenantService';

// ── Types ─────────────────────────────────────────────────────────────────────
interface PgListing {
  id: string;
  organization_id: string;
  city: string;
  locality: string;
  total_rooms: number;
  total_beds: number;
  asking_price: string | null;
  description: string;
  amenities: string;
  contact_phone: string | null;
  is_active: boolean;
  is_owner: boolean;
  created_at: string;
}

interface ListingsResponse {
  is_subscribed: boolean;
  results: PgListing[];
}

interface FormState {
  city: string;
  locality: string;
  total_rooms: string;
  total_beds: string;
  asking_price: string;
  description: string;
  amenities: string;
  contact_phone: string;
}

const EMPTY_FORM: FormState = {
  city: '',
  locality: '',
  total_rooms: '',
  total_beds: '',
  asking_price: '',
  description: '',
  amenities: '',
  contact_phone: '',
};

const PRIMARY = '#276859';
const SECONDARY = '#FBBD23';

// ── Main Component ────────────────────────────────────────────────────────────
const PgMarketplace: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<'browse' | 'mine'>('browse');
  const [data, setData] = useState<ListingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [detailListing, setDetailListing] = useState<PgListing | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getPgSaleListings();
      setData(res);
    } catch {
      setError('Failed to load listings. Please try again.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const allBrowseListings = data?.results.filter(l => !l.is_owner) ?? [];
  const myListings = data?.results.filter(l => l.is_owner) ?? [];
  const isSubscribed = data?.is_subscribed ?? false;

  const cities = Array.from(new Set(allBrowseListings.map(l => l.city).filter(Boolean))).sort();
  const browseListings = selectedCity
    ? allBrowseListings.filter(l => l.city === selectedCity)
    : allBrowseListings;

  // ── Form helpers ─────────────────────────────────────────────────────────────
  function openCreate() {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  }

  function openEdit(listing: PgListing) {
    setEditingId(listing.id);
    setForm({
      city: listing.city,
      locality: listing.locality,
      total_rooms: String(listing.total_rooms),
      total_beds: String(listing.total_beds),
      asking_price: listing.asking_price ?? '',
      description: listing.description,
      amenities: listing.amenities,
      contact_phone: listing.contact_phone ?? '',
    });
    setFormError('');
    setShowForm(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.city || !form.locality || !form.total_rooms || !form.total_beds || !form.contact_phone) {
      setFormError('City, Locality, Rooms, Beds and Contact are required.');
      return;
    }
    if (!/^\d{10}$/.test(form.contact_phone)) {
      setFormError('Contact must be a 10-digit phone number.');
      return;
    }
    setFormError('');
    setSaving(true);
    try {
      const payload = {
        city: form.city.trim(),
        locality: form.locality.trim(),
        total_rooms: parseInt(form.total_rooms),
        total_beds: parseInt(form.total_beds),
        asking_price: form.asking_price ? parseFloat(form.asking_price) : null,
        description: form.description.trim(),
        amenities: form.amenities.trim(),
        contact_phone: form.contact_phone.trim(),
      };
      if (editingId) {
        await updatePgSaleListing(editingId, payload);
      } else {
        await createPgSaleListing(payload);
      }
      setShowForm(false);
      setTab('mine');
      load();
    } catch {
      setFormError('Failed to save listing. Please try again.');
    }
    setSaving(false);
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await deletePgSaleListing(id);
      load();
    } catch {
      setError('Failed to remove listing.');
    }
    setDeletingId(null);
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="bg-[#F8FAF8] -m-6 md:-m-8 min-h-[calc(100vh-65px)]">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#276859] to-[#2A5248] text-white px-6 py-8">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold">PG Marketplace</h1>
              <p className="text-white/70 text-sm mt-1">Buy or sell PG properties with other verified hosts</p>
              {!isSubscribed && (
                <div className="mt-3 inline-flex items-center gap-2 bg-[#FBBD23]/20 border border-[#FBBD23]/40 rounded-full px-3 py-1">
                  <Lock className="h-3.5 w-3.5 text-[#FBBD23]" />
                  <span className="text-xs text-[#FBBD23] font-medium">Subscribe to view contact details</span>
                </div>
              )}
            </div>
            <Button
              onClick={openCreate}
              className="bg-[#FBBD23] hover:bg-[#e6a800] text-[#276859] font-bold flex items-center gap-2 shadow-lg"
            >
              <PlusCircle className="h-4 w-4" />
              Post My PG
            </Button>
          </div>

          {/* Stats */}
          <div className="flex gap-6 mt-6">
            <div className="text-center">
              <p className="text-2xl font-bold">{data?.results.length ?? '—'}</p>
              <p className="text-white/60 text-xs">Total Listings</p>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <p className="text-2xl font-bold">{myListings.length}</p>
              <p className="text-white/60 text-xs">My Listings</p>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <p className="text-2xl font-bold">{isSubscribed ? '✓' : '—'}</p>
              <p className="text-white/60 text-xs">Subscription</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-6">
        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border border-gray-100 mb-6 w-fit">
          {(['browse', 'mine'] as const).map(t => (
            <button
              key={t}
              onClick={() => { setTab(t); if (t === 'browse') setSelectedCity(''); }}
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${
                tab === t
                  ? 'bg-[#276859] text-white shadow'
                  : 'text-gray-500 hover:text-[#276859]'
              }`}
            >
              {t === 'browse' ? `Browse (${browseListings.length})` : `My Listings (${myListings.length})`}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl p-4 mb-4 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
            <button onClick={load} className="ml-auto text-red-500 hover:text-red-700">
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Loading */}
        {loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-10 w-10 animate-spin text-[#276859]" />
          </div>
        ) : (
          <>
            {/* Subscription banner for non-subscribed */}
            {!isSubscribed && tab === 'browse' && browseListings.length > 0 && (
              <button
                onClick={() => navigate('/subscription')}
                className="w-full text-left bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-start gap-3 hover:bg-amber-100 transition-colors"
              >
                <Lock className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
                <div>
                  <p className="font-semibold text-amber-800 text-sm">Contact details are hidden</p>
                  <p className="text-amber-700 text-xs mt-0.5">
                    Upgrade your subscription to reveal seller contact numbers and connect directly.
                  </p>
                  <p className="text-amber-600 text-xs font-semibold mt-1 underline underline-offset-2">View plans →</p>
                </div>
              </button>
            )}

            {/* Browse Tab */}
            {tab === 'browse' && (
              <>
                {/* City filter pills */}
                {cities.length > 0 && (
                  <div className="flex gap-2 overflow-x-auto pb-1 mb-4 scrollbar-hide">
                    <button
                      onClick={() => setSelectedCity('')}
                      className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                        selectedCity === ''
                          ? 'bg-[#276859] text-white border-[#276859]'
                          : 'bg-white text-gray-500 border-gray-200 hover:border-[#276859] hover:text-[#276859]'
                      }`}
                    >
                      All Cities
                    </button>
                    {cities.map(city => (
                      <button
                        key={city}
                        onClick={() => setSelectedCity(city)}
                        className={`shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold border transition-all ${
                          selectedCity === city
                            ? 'bg-[#276859] text-white border-[#276859]'
                            : 'bg-white text-gray-500 border-gray-200 hover:border-[#276859] hover:text-[#276859]'
                        }`}
                      >
                        {city}
                      </button>
                    ))}
                  </div>
                )}

                {browseListings.length === 0 ? (
                  <EmptyState
                    message={selectedCity ? `No listings in ${selectedCity}.` : "No PG listings available right now."}
                    hint={selectedCity ? "Try a different city or view all listings." : "Check back later or be the first to post your PG for sale."}
                  />
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {browseListings.map(l => (
                      <ListingCard key={l.id} listing={l} isSubscribed={isSubscribed}
                        onViewDetail={isSubscribed ? () => setDetailListing(l) : undefined}
                        onSubscribe={() => navigate('/subscription')} />
                    ))}
                  </div>
                )}
              </>
            )}

            {/* My Listings Tab */}
            {tab === 'mine' && (
              myListings.length === 0 ? (
                <EmptyState
                  message="You haven't posted any listings yet."
                  hint='Click "Post My PG" to list your PG for sale.'
                />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {myListings.map(l => (
                    <ListingCard
                      key={l.id}
                      listing={l}
                      isSubscribed={true}
                      isOwner
                      onEdit={() => openEdit(l)}
                      onDelete={() => handleDelete(l.id)}
                      deleting={deletingId === l.id}
                      onViewDetail={() => setDetailListing(l)}
                    />
                  ))}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {detailListing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-[#276859] to-[#2A5248] px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">{detailListing.locality}</h2>
                <p className="text-white/70 text-sm">{detailListing.city}</p>
              </div>
              <button onClick={() => setDetailListing(null)} className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {/* Stats */}
              <div className="flex gap-3 flex-wrap">
                <StatPill icon={<DoorOpen className="h-3.5 w-3.5" />} label={`${detailListing.total_rooms} Rooms`} />
                <StatPill icon={<BedDouble className="h-3.5 w-3.5" />} label={`${detailListing.total_beds} Beds`} />
                {detailListing.asking_price && (
                  <StatPill icon={<BadgeIndianRupee className="h-3.5 w-3.5" />}
                    label={`₹${Number(detailListing.asking_price).toLocaleString('en-IN')}`} highlight />
                )}
              </div>

              {/* All amenities */}
              {detailListing.amenities && (
                <div>
                  <p className="text-xs font-semibold text-[#276859] uppercase tracking-wide mb-2">Amenities</p>
                  <div className="flex flex-wrap gap-1.5">
                    {detailListing.amenities.split(',').map(a => a.trim()).filter(Boolean).map(a => (
                      <span key={a} className="bg-[#276859]/8 text-[#276859] text-xs px-2.5 py-1 rounded-full border border-[#276859]/15">
                        {a}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Full description */}
              {detailListing.description && (
                <div>
                  <p className="text-xs font-semibold text-[#276859] uppercase tracking-wide mb-2">Description</p>
                  <ExpandableText text={detailListing.description} className="text-gray-600 text-sm leading-relaxed" />
                </div>
              )}

              {/* Contact */}
              <div className="border-t border-gray-100 pt-4">
                <a
                  href={`tel:${detailListing.contact_phone}`}
                  className="flex items-center justify-center gap-2 w-full bg-[#276859] text-white font-bold py-3 rounded-xl hover:bg-[#1f5246] transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  Call Seller — {detailListing.contact_phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Post / Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="bg-gradient-to-r from-[#276859] to-[#2A5248] px-6 py-4 rounded-t-2xl flex items-center justify-between">
              <h2 className="text-white font-bold text-lg">
                {editingId ? 'Edit Listing' : 'Post PG for Sale'}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-white/70 hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  <AlertCircle className="h-4 w-4 shrink-0" />
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <FormField label="City *" placeholder="e.g. Bangalore"
                  value={form.city} onChange={v => setForm(f => ({ ...f, city: v }))} />
                <FormField label="Locality *" placeholder="e.g. Koramangala"
                  value={form.locality} onChange={v => setForm(f => ({ ...f, locality: v }))} />
                <FormField label="Total Rooms *" placeholder="e.g. 10" type="number"
                  value={form.total_rooms} onChange={v => setForm(f => ({ ...f, total_rooms: v }))} />
                <FormField label="Total Beds *" placeholder="e.g. 20" type="number"
                  value={form.total_beds} onChange={v => setForm(f => ({ ...f, total_beds: v }))} />
                <FormField label="Asking Price (₹)" placeholder="e.g. 5000000" type="number"
                  value={form.asking_price} onChange={v => setForm(f => ({ ...f, asking_price: v }))} />
                <FormField label="Contact Number *" placeholder="10-digit mobile"
                  value={form.contact_phone} onChange={v => setForm(f => ({ ...f, contact_phone: v }))} />
              </div>

              <FormField label="Amenities" placeholder="e.g. WiFi, Parking, CCTV, Generator"
                value={form.amenities} onChange={v => setForm(f => ({ ...f, amenities: v }))} />

              <div>
                <label className="block text-sm font-semibold text-[#276859] mb-1">Description</label>
                <textarea
                  rows={3}
                  placeholder="Brief details about the PG, occupancy, location advantages..."
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]"
                />
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                Your contact number will only be visible to subscribed hosts.
              </div>

              <div className="flex gap-3 pt-2">
                <Button type="button" variant="outline"
                  className="flex-1 border-[#276859] text-[#276859]"
                  onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button type="submit"
                  className="flex-[2] bg-[#276859] hover:bg-[#1f5246] text-white font-bold"
                  disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {saving ? 'Saving…' : editingId ? 'Update Listing' : 'Post Listing'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Listing Card ──────────────────────────────────────────────────────────────
const ListingCard: React.FC<{
  listing: PgListing;
  isSubscribed: boolean;
  isOwner?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
  onViewDetail?: () => void;
  onSubscribe?: () => void;
}> = ({ listing, isSubscribed, isOwner, onEdit, onDelete, deleting, onViewDetail, onSubscribe }) => {
  const amenityList = listing.amenities
    ? listing.amenities.split(',').map(a => a.trim()).filter(Boolean)
    : [];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
      {/* Top accent */}
      <div className="h-1 bg-gradient-to-r from-[#276859] to-[#FBBD23]" />

      <div className="p-5">
        {/* Location */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-1.5 text-[#276859]">
              <MapPin className="h-4 w-4 shrink-0" />
              <span className="font-bold text-base">{listing.locality}</span>
            </div>
            <p className="text-gray-400 text-xs ml-6">{listing.city}</p>
          </div>
          {isOwner && (
            <span className="bg-green-100 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Your listing
            </span>
          )}
        </div>

        {/* Stats row */}
        <div className="flex gap-3 mb-3">
          <StatPill icon={<DoorOpen className="h-3.5 w-3.5" />} label={`${listing.total_rooms} Rooms`} />
          <StatPill icon={<BedDouble className="h-3.5 w-3.5" />} label={`${listing.total_beds} Beds`} />
          {listing.asking_price && (
            <StatPill
              icon={<BadgeIndianRupee className="h-3.5 w-3.5" />}
              label={`₹${Number(listing.asking_price).toLocaleString('en-IN')}`}
              highlight
            />
          )}
        </div>

        {/* Amenities — max 4, rest hidden */}
        {amenityList.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {amenityList.slice(0, 4).map(a => (
              <span key={a} className="bg-[#276859]/8 text-[#276859] text-xs px-2 py-0.5 rounded-full border border-[#276859]/15">
                {a}
              </span>
            ))}
            {amenityList.length > 4 && (
              <span className="text-gray-400 text-xs px-1">+{amenityList.length - 4} more</span>
            )}
          </div>
        )}

        {/* Description — hidden; subscribed users get "View Details" */}
        {!isSubscribed ? (
          <button
            onClick={onSubscribe}
            className="w-full flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3 hover:bg-amber-100 transition-colors text-left"
          >
            <Lock className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span className="text-amber-700 text-xs">Subscribe to view description & contact</span>
          </button>
        ) : (
          <button
            onClick={onViewDetail}
            className="flex items-center gap-1.5 text-[#276859] text-xs font-semibold mb-3 hover:underline"
          >
            <Eye className="h-3.5 w-3.5" />
            View full details
          </button>
        )}

        {/* Contact / Actions — only shown for subscribed or owners */}
        {(isSubscribed || isOwner) && (
          <div className="border-t border-gray-100 pt-3 flex items-center justify-between">
            <a
              href={`tel:${listing.contact_phone}`}
              className="flex items-center gap-2 bg-[#276859] text-white text-sm font-semibold px-4 py-2 rounded-lg hover:bg-[#1f5246] transition-colors"
            >
              <Phone className="h-3.5 w-3.5" />
              {listing.contact_phone}
            </a>

            {isOwner && (
              <div className="flex gap-2">
                <button
                  onClick={onEdit}
                  className="p-2 rounded-lg text-[#276859] hover:bg-[#276859]/10 transition-colors"
                  title="Edit listing"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={onDelete}
                  disabled={deleting}
                  className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors"
                  title="Remove listing"
                >
                  {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Small helpers ─────────────────────────────────────────────────────────────
const StatPill: React.FC<{ icon: React.ReactNode; label: string; highlight?: boolean }> = ({ icon, label, highlight }) => (
  <div className={`flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full
    ${highlight ? 'bg-[#FBBD23]/15 text-[#856404]' : 'bg-[#276859]/8 text-[#276859]'}`}>
    {icon}
    {label}
  </div>
);

const FormField: React.FC<{
  label: string; placeholder: string; value: string;
  onChange: (v: string) => void; type?: string;
}> = ({ label, placeholder, value, onChange, type = 'text' }) => (
  <div>
    <label className="block text-sm font-semibold text-[#276859] mb-1">{label}</label>
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#276859]"
    />
  </div>
);

const EmptyState: React.FC<{ message: string; hint: string }> = ({ message, hint }) => (
  <div className="text-center py-20 text-gray-400">
    <DoorOpen className="h-14 w-14 mx-auto mb-4 opacity-30" />
    <p className="font-semibold text-gray-600 text-base">{message}</p>
    <p className="text-sm mt-1">{hint}</p>
  </div>
);

export default PgMarketplace;
